import React, { useState, useRef, useCallback } from 'react'
import { Button } from './Button'
import { Card, CardContent } from './Card'
import { Upload, File, X, Check, AlertCircle, FileText, Table } from 'lucide-react'

interface FileUploadProps {
  onFileUpload?: (file: File, data: any) => void
  acceptedTypes?: string[]
  maxSizeBytes?: number
  showPreview?: boolean
  sampleDatasets?: Array<{
    name: string
    description: string
    filename: string
    data: any[]
  }>
}

interface UploadedFile {
  file: File
  data?: any
  preview?: string
  status: 'uploading' | 'success' | 'error'
  error?: string
}

export function FileUpload({
  onFileUpload,
  acceptedTypes = ['.csv', '.json', '.xlsx'],
  maxSizeBytes = 5 * 1024 * 1024, // 5MB
  showPreview = true,
  sampleDatasets = []
}: FileUploadProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const processFile = useCallback(async (file: File): Promise<any> => {
    const extension = file.name.toLowerCase().split('.').pop()
    const text = await file.text()

    switch (extension) {
      case 'csv':
        return parseCSV(text)
      case 'json':
        return JSON.parse(text)
      default:
        throw new Error(`Unsupported file type: ${extension}`)
    }
  }, [])

  const parseCSV = (text: string) => {
    const lines = text.trim().split('\n')
    if (lines.length === 0) return []

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''))
    const data = lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''))
      const obj: any = {}
      headers.forEach((header, index) => {
        const value = values[index] || ''
        // Try to parse as number if it looks like one
        if (/^\d+(\.\d+)?$/.test(value)) {
          obj[header] = parseFloat(value)
        } else {
          obj[header] = value
        }
      })
      return obj
    })

    return data
  }

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const newFiles: UploadedFile[] = Array.from(files).map(file => ({
      file,
      status: 'uploading' as const
    }))

    setUploadedFiles(prev => [...prev, ...newFiles])

    // Process each file
    for (let i = 0; i < newFiles.length; i++) {
      const fileItem = newFiles[i]
      
      try {
        // Validate file size
        if (fileItem.file.size > maxSizeBytes) {
          throw new Error(`File size exceeds ${Math.round(maxSizeBytes / 1024 / 1024)}MB limit`)
        }

        // Validate file type
        const extension = '.' + fileItem.file.name.toLowerCase().split('.').pop()
        if (!acceptedTypes.includes(extension)) {
          throw new Error(`File type ${extension} not supported. Accepted types: ${acceptedTypes.join(', ')}`)
        }

        // Process the file
        const data = await processFile(fileItem.file)
        
        setUploadedFiles(prev => prev.map(f => 
          f.file === fileItem.file 
            ? { ...f, status: 'success', data, preview: generatePreview(data) }
            : f
        ))

        onFileUpload?.(fileItem.file, data)

      } catch (error) {
        setUploadedFiles(prev => prev.map(f => 
          f.file === fileItem.file 
            ? { ...f, status: 'error', error: error instanceof Error ? error.message : 'Unknown error' }
            : f
        ))
      }
    }
  }

  const generatePreview = (data: any[]): string => {
    if (!Array.isArray(data) || data.length === 0) return 'No data found'
    
    const firstRow = data[0]
    const columns = Object.keys(firstRow)
    const preview = `${data.length} rows, ${columns.length} columns\nColumns: ${columns.slice(0, 5).join(', ')}${columns.length > 5 ? '...' : ''}`
    
    return preview
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileSelect(e.dataTransfer.files)
  }

  const removeFile = (file: File) => {
    setUploadedFiles(prev => prev.filter(f => f.file !== file))
  }

  const loadSampleDataset = (dataset: any) => {
    // Create a mock file object that behaves like a File but works in all environments
    const mockFileContent = JSON.stringify(dataset.data)
    const mockFile = {
      name: dataset.filename,
      size: mockFileContent.length,
      type: 'application/json',
      lastModified: Date.now(),
      slice: () => new Blob([mockFileContent]),
      stream: () => new ReadableStream(),
      text: () => Promise.resolve(mockFileContent),
      arrayBuffer: () => Promise.resolve(new ArrayBuffer(0))
    } as File
    
    const uploadedFile: UploadedFile = {
      file: mockFile,
      data: dataset.data,
      preview: generatePreview(dataset.data),
      status: 'success'
    }
    
    setUploadedFiles(prev => [...prev, uploadedFile])
    onFileUpload?.(mockFile, dataset.data)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card>
        <CardContent className="p-6">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-primary bg-primary/10'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <Upload className={`w-12 h-12 mx-auto mb-4 ${
              isDragging ? 'text-primary' : 'text-muted-foreground'
            }`} />
            
            <h3 className="text-lg font-medium mb-2">Upload Your Data</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drag and drop your files here, or click to browse
            </p>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="gap-2"
            >
              <File className="w-4 h-4" />
              Choose Files
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={acceptedTypes.join(',')}
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
            />
            
            <p className="text-xs text-muted-foreground mt-4">
              Supported formats: {acceptedTypes.join(', ')} • Max size: {Math.round(maxSizeBytes / 1024 / 1024)}MB
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sample Datasets */}
      {sampleDatasets.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-medium mb-4">Try Sample Datasets</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {sampleDatasets.map((dataset, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => loadSampleDataset(dataset)}
                >
                  <div className="flex items-start gap-3">
                    <Table className="w-5 h-5 text-primary mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium">{dataset.name}</h4>
                      <p className="text-sm text-muted-foreground">{dataset.description}</p>
                      <div className="text-xs text-muted-foreground mt-2">
                        {Array.isArray(dataset.data) ? `${dataset.data.length} rows` : 'Sample data'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-medium mb-4">Uploaded Files</h3>
            <div className="space-y-3">
              {uploadedFiles.map((fileItem, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {fileItem.status === 'uploading' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                    )}
                    {fileItem.status === 'success' && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {fileItem.status === 'error' && (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium truncate">{fileItem.file.name}</span>
                      <span className="text-sm text-muted-foreground">
                        {formatFileSize(fileItem.file.size)}
                      </span>
                    </div>
                    
                    {fileItem.status === 'success' && fileItem.preview && showPreview && (
                      <p className="text-sm text-muted-foreground">
                        {fileItem.preview}
                      </p>
                    )}
                    
                    {fileItem.status === 'error' && fileItem.error && (
                      <p className="text-sm text-red-600">{fileItem.error}</p>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => removeFile(fileItem.file)}
                    size="sm"
                    variant="ghost"
                    className="flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}